import React, { forwardRef } from "react";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { TONES } from "../../lib/tones.js";

/* ---------------- TrendIndicator ---------------- */

/** The subset of StatusTone a trend can mean: `healthy`/`critical` colour a
 *  favourable/unfavourable delta, `watch` flags a caution reading, and
 *  `neutral` is the flat state. No `info` or `ai` — those aren't trend
 *  outcomes. */
export type TrendIndicatorTone = "healthy" | "watch" | "critical" | "neutral";

const ICON_SIZE: Record<"sm" | "md", string> = {
  sm: "w-2.5 h-2.5",
  md: "w-3 h-3",
};

export interface TrendIndicatorProps extends React.HTMLAttributes<HTMLSpanElement> {
  delta: number;
  unit?: React.ReactNode;
  /** When true, lower is better (e.g. defects). Inverts color. */
  inverse?: boolean | undefined;
  /** Overrides the derived healthy/watch/critical/neutral tone. The colour
   *  is the tone's `text` entry in lib/tones.ts. */
  tone?: TrendIndicatorTone | undefined;
  /** Replaces the formatted `{abs(delta)}{unit}`. */
  children?: React.ReactNode;
  /** `md` (default) is today's size; `sm` is `text-dense-1` with a 10px icon. */
  size?: "sm" | "md" | undefined;
}

export const TrendIndicator = forwardRef<HTMLSpanElement, TrendIndicatorProps>(
  function TrendIndicator(
    {
      delta,
      unit = "%",
      inverse,
      tone,
      size = "md",
      className,
      children,
      ...rest
    },
    ref,
  ) {
    const dir = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
    const good = inverse ? delta < 0 : delta > 0;
    const toneClass = tone
      ? TONES[tone].text
      : dir === "flat"
        ? TONES.neutral.text
        : good
          ? TONES.healthy.text
          : TONES.critical.text;
    const Icon = dir === "up" ? ArrowUp : dir === "down" ? ArrowDown : Minus;
    const hasAriaLabel = rest["aria-label"] != null;
    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center gap-0.5 text-xs font-medium pb-tabular",
          size === "sm" && "text-dense-1",
          toneClass,
          className,
        )}
        {...rest}
      >
        <Icon aria-hidden="true" className={ICON_SIZE[size]} />
        {!hasAriaLabel && <span className="sr-only">{dir} </span>}
        {children ?? (
          <>
            {Math.abs(delta)}
            {unit}
          </>
        )}
      </span>
    );
  },
);
